import { LoadingOutlined } from '@ant-design/icons';
import AppDescription from '@nxsys/components/AppDescription';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeMessageActions } from '@nxsys/store/slices/employeeMessage/reducer/employeeMessageSlice';
import { fetchUnreadCount } from '@nxsys/store/slices/message/actions';
import { Icons } from '@nxsys/types/icons';
import {
  ChatMessage,
  Conversation,
  ConversationPriority,
} from '@nxsys/store/slices/message/types';
import { formatStatus } from '@nxsys/utils';
import { Descriptions, Tag } from 'antd';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import ChatMessageList from '../../../message/components/ChatMessageList';
import { StyledMessagePage } from '../../../message/index.styled';

const priorityLabels: Record<ConversationPriority, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

type ViewMessageHistoryModalProps = {
  isOpen: boolean;
  toggle: () => void;
  data: Conversation;
  onMessageSent?: () => void;
};

const ViewMessageHistoryModal: React.FC<ViewMessageHistoryModalProps> = ({
  isOpen,
  toggle,
  data,
  onMessageSent,
}) => {
  const { messages: intlMessages } = useTypedIntl();
  const { user, isBureau } = useSelector((state: AppState) => state.appStore);
  const dispatch = useDispatch<AppDispatch>();
  const {
    messages,
    messagesLoading,
    messagesNextCursor,
    sendingMessage,
    activeConversation,
  } = useSelector((state: AppState) => state.employeeMessageStore);
  const currentAgencyId = (user as any)?.agencyId?.id;

  useEffect(() => {
    if (isOpen && data?.id) {
      dispatch(employeeMessageActions.setActiveConversation(data));
      dispatch(
        employeeMessageActions.eeLoadMessages({
          conversationId: data.id,
          limit: 20,
        }),
      );

      // Mark as read
      if (user?.id) {
        dispatch(
          employeeMessageActions.eeMarkConversationRead({
            id: data.id,
            userId: user.id,
            agencyId: currentAgencyId,
          }),
        );
        dispatch(fetchUnreadCount());
      }
    }

    return () => {
      if (!isOpen) {
        dispatch(employeeMessageActions.setActiveConversation(null));
      }
    };
  }, [isOpen, data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto mark-as-read when new messages arrive via socket
  const prevMessagesLengthRef = useRef(0);
  useEffect(() => {
    if (
      isOpen &&
      messages.length > 0 &&
      messages.length > prevMessagesLengthRef.current &&
      user?.id
    ) {
      dispatch(
        employeeMessageActions.eeMarkConversationRead({
          id: data.id,
          userId: user.id,
          agencyId: currentAgencyId,
        }),
      );
      dispatch(fetchUnreadCount());
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    prevMessagesLengthRef.current = 0;
  }, [data?.id]);

  // Load more older messages
  const handleLoadMoreMessages = useCallback(() => {
    if (!messagesNextCursor || messagesLoading) return;
    dispatch(
      employeeMessageActions.eeLoadMessages({
        conversationId: data.id,
        cursor: messagesNextCursor,
        limit: 20,
      }),
    );
  }, [dispatch, data.id, messagesNextCursor, messagesLoading]);

  // maxOtherReadAt for read receipts
  const maxOtherReadAt = useMemo(() => {
    const conv = activeConversation || data;
    if (!conv?.participants) return null;
    const currentUserId = user?.id;

    const isCurrentUser = (p: (typeof conv.participants)[0]) => {
      if (p.userId && String(p.userId) === String(currentUserId)) return true;
      if (
        !p.userId &&
        currentAgencyId &&
        String(p.agencyId) === String(currentAgencyId)
      )
        return true;
      if (p.isCompanyParentBroadcast) return true;
      return false;
    };

    const otherParticipants = (conv.participants || []).filter(
      (p) => !isCurrentUser(p),
    );
    const readAts = otherParticipants
      .map((p) => p.lastReadAt)
      .filter(Boolean) as string[];

    if (!readAts.length) return null;
    return readAts.reduce((max, d) => (d > max ? d : max));
  }, [
    activeConversation?.participants,
    data?.participants,
    user?.id,
    currentAgencyId,
  ]);

  const [messageValue, setMessageValue] = useState('');

  const handleSendMessage = () => {
    if (!messageValue.trim() || sendingMessage) return;
    const uuid = uuidv4();

    const optimisticMsg: ChatMessage = {
      id: 0,
      conversationId: data.id,
      uuid,
      content: messageValue,
      senderUserId: user?.id || '',
      createdAt: new Date().toISOString(),
      deletedAt: null,
      sender: {
        id: user?.id || '',
        forename: (user as any)?.forename || (user as any)?.firstName || '',
        surname: (user as any)?.surname || (user as any)?.lastName || '',
      },
      _status: 'sending',
      _tempId: uuid,
    };

    dispatch(employeeMessageActions.addOptimisticMessage(optimisticMsg));
    dispatch(
      employeeMessageActions.eeSendMessage({
        conversationId: data.id,
        content: messageValue,
        uuid,
      }),
    );
    setMessageValue('');
    onMessageSent?.();
  };

  const conversationForList = activeConversation || data;

  return (
    <AppModalContainer
      title={
        <>
          <span>{intlMessages['common.message']}</span>
          <span className={`${formatStatus(data.status)}`}>
            <AppTagStatus className={`tag-popup ${formatStatus(data.status)}`}>
              {formatStatus(data.status)}
            </AppTagStatus>
          </span>
        </>
      }
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: intlMessages['common.close'],
          variant: 'outlined',
          onClick: toggle,
        },
      ]}
      footer={
        <AppInput
          value={messageValue}
          onChange={(e) => setMessageValue(e.target.value)}
          placeholder={intlMessages['form.typeAnything']}
          onPressEnter={handleSendMessage}
          suffix={
            <span className="cursor-pointer" onClick={handleSendMessage}>
              {sendingMessage ? <LoadingOutlined spin /> : <Icons.IconSend />}
            </span>
          }
        />
      }
    >
      <div className="p-[16px] border border-neutral-100 rounded-[8px]">
        <AppDescription
          column={1}
          labelStyle={{ width: 216 }}
          bordered={false}
          colon={false}
          size="middle"
        >
          <Descriptions.Item label={intlMessages['common.subject']}>
            {data.subject}
          </Descriptions.Item>
          <Descriptions.Item label={intlMessages['common.priority']}>
            {data.priority ? (
              <Tag>{priorityLabels[data.priority] || data.priority}</Tag>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label={intlMessages['common.description']}>
            {data.description}
          </Descriptions.Item>
          <Descriptions.Item label={intlMessages['common.dueDate']}>
            {data.dueDate
              ? moment(data.dueDate).tz('Europe/London').format('DD/MM/YYYY')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={intlMessages['common.createdOn']}>
            {data.createdAt
              ? moment(data.createdAt).tz('Europe/London').format('DD/MM/YYYY')
              : '-'}
          </Descriptions.Item>
        </AppDescription>
      </div>
      <StyledMessagePage>
        <div className="text-[18px] font-bold py-[16px] mb-[8px] border-b border-neutral-100">
          {intlMessages['common.response']}
        </div>
        <div className="h-[400px] flex flex-col overflow-hidden">
          <ChatMessageList
            messages={messages}
            currentUserId={user?.id || ''}
            conversation={conversationForList}
            currentUser={user}
            isBureau={isBureau}
            messagesLoading={messagesLoading}
            hasMoreMessages={!!messagesNextCursor}
            maxOtherReadAt={maxOtherReadAt}
            onLoadMoreMessages={handleLoadMoreMessages}
          />
        </div>
      </StyledMessagePage>
    </AppModalContainer>
  );
};

export default ViewMessageHistoryModal;
